/**
 * Perimeter API Service (Frontend)
 *
 * HTTP client for the perimeter management API.
 * Supports upload, current perimeter retrieval, and historic perimeter listing.
 *
 * Uses relative /api/* paths — proxied to backend by Vite in dev and
 * by nginx in staging/production. Do NOT prepend a BACKEND_URL here.
 */
import type { PerimeterRecord, HistoricPerimeterRecord } from '../types/perimeter';

interface PerimeterUploadMeta {
  description?: string;
  sourceName?: string;
}

/**
 * Upload a new perimeter file for a fire.
 * Sends multipart FormData to POST /api/perimeters/upload/:fireNumber
 */
export async function uploadPerimeter(
  fireNumber: string,
  file: File,
  meta: PerimeterUploadMeta
): Promise<PerimeterRecord> {
  const url = `/api/perimeters/upload/${encodeURIComponent(fireNumber)}`;

  const form = new FormData();
  form.append('file', file);
  if (meta.description) form.append('description', meta.description);
  if (meta.sourceName) form.append('sourceName', meta.sourceName);

  const response = await fetch(url, {
    method: 'POST',
    body: form,
    credentials: 'include',
  });

  if (!response.ok) {
    // Surface the backend's error string (e.g. the perimeter parse failure
    // message) instead of a bare HTTP status, so the modal can show it.
    let message = `Upload failed (HTTP ${response.status})`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch { /* not JSON */ }
    throw new Error(message);
  }

  const json = await response.json();
  return json as PerimeterRecord;
}

/**
 * Fetch the current (active) perimeter for a fire.
 * Returns null if no perimeter is on file (404).
 */
export async function getCurrentPerimeter(
  fireNumber: string
): Promise<PerimeterRecord | null> {
  const url = `/api/perimeters/current/${encodeURIComponent(fireNumber)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const json = await response.json();
  return json as PerimeterRecord;
}

/**
 * Delete a perimeter by ID. Strict cascade: the backend will fail the
 * delete if the linked CMS file cannot be removed.
 */
export async function deletePerimeter(id: string): Promise<void> {
  const response = await fetch(`/api/perimeters/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    let message = `Delete failed (HTTP ${response.status})`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch { /* not JSON */ }
    throw new Error(message);
  }
}

/**
 * Fetch the historic perimeter archive for a fire.
 * Returns oldest-first array (sorted server-side).
 */
export async function getHistoricPerimeters(
  fireNumber: string
): Promise<HistoricPerimeterRecord[]> {
  const url = `/api/perimeters/history/${encodeURIComponent(fireNumber)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const json = await response.json();
  return (json.perimeters as HistoricPerimeterRecord[]) ?? [];
}

/** Fire history polygon from GeoServer */
export interface FireHistoryRecord {
  fireid: string;
  fireyear: number;
  effectdate: string;
  area_ha: number;
  perimtype: string;
  region: string;
  geometry: GeoJSON.MultiPolygon;
}

/**
 * Fetch the official fire history polygon for a fire.
 * Returns null if no record exists (404) or GeoServer is unreachable.
 */
export async function getFireHistoryPolygon(
  fireNumber: string
): Promise<FireHistoryRecord | null> {
  const url = `/api/perimeters/fire-history/${encodeURIComponent(fireNumber)}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      return null;
    }

    return await response.json() as FireHistoryRecord;
  } catch {
    // Fire history failure is non-fatal
    return null;
  }
}
